'use client'

import React, { useMemo, useState } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { ChevronDown, HardDrive } from 'lucide-react'

import type { RouterOutputs } from '@package/api'
import { Button, Skeleton } from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import { formatDate } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import { useSharedTRPC } from '../../../context'

type ReadingHistoryRow = RouterOutputs['metrics']['getReadingHistory']['data'][number]

/** Indonesian reading duration: "<1 menit", "5 menit", "2 jam", "2 jam 9 menit". */
function formatReadingDuration(totalSeconds: number): string {
  if (totalSeconds < 60) return '<1 menit'
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const parts: string[] = []
  if (hours > 0) parts.push(`${hours} jam`)
  if (minutes > 0) parts.push(`${minutes} menit`)
  return parts.join(' ')
}

function readingStatus(row: ReadingHistoryRow): string {
  if (row.isCompleted) return 'Selesai'
  const minutes = Math.floor(row.duration / 60)
  return minutes < 2 ? 'Hanya lihat-lihat' : 'Sedang dibaca'
}

interface ReadingHistoryProps {
  childId: string
  /** Same window as the ReadingOverview card so the list and the "X dari Y buku" stat cover the same period. */
  from?: Date
  to?: Date
  className?: string
}

// Row grid: cover | title+author | duration | status. min-width keeps columns aligned; the
// list scrolls horizontally on narrow screens instead of squishing (mirrors the old table).
const rowGridClassName =
  'srd:grid srd:min-w-[640px] srd:grid-cols-[auto_minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,1.2fr)] srd:items-center srd:gap-4 srd:px-4 srd:py-3'

function ReadingHistoryItem({ row }: { row: ReadingHistoryRow }) {
  const status = readingStatus(row)

  return (
    <div className={rowGridClassName}>
      {row.coverUrl ? (
        <img
          src={row.coverUrl}
          alt={row.bookTitle}
          width={44}
          height={59}
          draggable={false}
          className='srd:aspect-3/4 srd:w-14 srd:shrink-0 srd:rounded-sm srd:object-cover'
        />
      ) : (
        <div className='srd:aspect-3/4 srd:w-14 srd:shrink-0 srd:rounded-sm srd:bg-grey-10' />
      )}

      <div className='srd:flex srd:min-w-0 srd:flex-col'>
        <p className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:line-clamp-2 srd:text-neutral-black')}>
          {row.bookTitle}
        </p>
        {row.author && (
          <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:line-clamp-1 srd:text-neutral-black')}>
            {row.author}
          </p>
        )}
      </div>

      <p className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:text-neutral-black')}>
        {formatReadingDuration(row.duration)}
      </p>

      <p className={cn(typographyVariants({ variant: 'label-md' }), 'srd:text-neutral-black')}>{status}</p>
    </div>
  )
}

interface ReadingHistoryListProps {
  groupedByDate: [string, ReadingHistoryRow[]][]
  isPending: boolean
  isError: boolean
  isEmpty: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean | undefined
  pageSize: number
  refetch: () => void
  fetchNextPage: () => void
  className?: string
}

const ReadingHistoryList = React.memo(function ReadingHistoryList({
  groupedByDate,
  isPending,
  isError,
  isEmpty,
  isFetchingNextPage,
  hasNextPage,
  pageSize,
  refetch,
  fetchNextPage,
  className,
}: ReadingHistoryListProps) {
  if (isError) {
    return (
      <div className='srd:flex srd:w-full srd:flex-col srd:items-center srd:justify-center srd:gap-2 srd:py-8'>
        <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-main-red-100')}>
          Gagal memuat riwayat baca
        </p>
        <Button size='sm' onClick={() => refetch()} variant='outline'>
          Coba lagi
        </Button>
      </div>
    )
  }

  if (isPending) {
    return (
      <div className={cn('srd:w-full srd:overflow-x-auto', className)}>
        <div className='srd:flex srd:flex-col srd:gap-1'>
          <Skeleton className='srd:h-9 srd:min-w-[640px] srd:rounded-lg' />
          <div className='srd:flex srd:flex-col srd:divide-y srd:divide-outline-variant/50'>
            {new Array(pageSize).fill(0).map((_, i) => (
              <div key={i} className={rowGridClassName}>
                <Skeleton className='srd:aspect-3/4 srd:w-14 srd:rounded-sm' />
                <div className='srd:flex srd:flex-col srd:gap-1'>
                  <Skeleton className='srd:h-4 srd:w-3/4' />
                  <Skeleton className='srd:h-3 srd:w-1/2' />
                </div>
                <Skeleton className='srd:h-4 srd:w-20' />
                <Skeleton className='srd:h-4 srd:w-24' />
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (isEmpty) {
    return (
      <div className='srd:flex srd:flex-col srd:items-center srd:justify-center srd:gap-2 srd:py-8 srd:text-grey-100'>
        <HardDrive />
        <p className={cn(typographyVariants({ variant: 'body-sm' }))}>Belum ada riwayat baca</p>
      </div>
    )
  }

  return (
    <div className={cn('srd:w-full srd:overflow-x-auto', className)}>
      <div className='srd:flex srd:flex-col srd:gap-4'>
        {groupedByDate.map(([dateKey, groupItems]) => (
          <div key={dateKey} className='srd:flex srd:flex-col srd:gap-1'>
            <div className='srd:min-w-[640px] srd:rounded-lg srd:bg-grey-30 srd:px-4 srd:py-2'>
              <p className={cn(typographyVariants({ variant: 'body-md' }), 'srd:font-bold srd:text-neutral-black')}>
                {dateKey}
              </p>
            </div>
            <div className='srd:flex srd:flex-col srd:divide-y srd:divide-outline-variant/50'>
              {groupItems.map((item) => (
                <ReadingHistoryItem key={`${item.bookTitle}-${item.date}-${item.duration}`} row={item} />
              ))}
            </div>
          </div>
        ))}

        {hasNextPage && (
          <div className='srd:flex srd:justify-center srd:py-2'>
            <Button size='sm' onClick={() => void fetchNextPage()} disabled={isFetchingNextPage}>
              {isFetchingNextPage ? 'Memuat...' : 'Muat lebih banyak'}
              <ChevronDown className='srd:size-4 srd:ml-2' />
            </Button>
          </div>
        )}
      </div>
    </div>
  )
})

export function ReadingHistory({ childId, from, to, className }: ReadingHistoryProps) {
  const trpc = useSharedTRPC()

  const [pageSize] = useState(10)

  // getReadingHistory windows on UTC start/end-of-day, matching getReadingMetrics (the card).
  const fromIso = from?.toISOString()
  const toIso = to?.toISOString()

  const input = useMemo(
    () =>
      ({
        userId: childId,
        pageSize,
        ...(fromIso ? { from: fromIso } : {}),
        ...(toIso ? { to: toIso } : {}),
      }) as const,
    [childId, pageSize, fromIso, toIso],
  )

  const { data, isPending, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery(
    trpc.metrics.getReadingHistory.infiniteQueryOptions(input, {
      getNextPageParam: (lastPage) => lastPage.nextCursor,
      initialCursor: 1,
    }),
  )

  const allRows = useMemo(
    () => data?.pages.flatMap((p) => (p as { data: RouterOutputs['metrics']['getReadingHistory']['data'] }).data) ?? [],
    [data],
  )

  const groupedByDate = useMemo(() => {
    if (allRows.length === 0) return []
    const groups: Record<string, ReadingHistoryRow[]> = {}
    for (const row of allRows) {
      if (!row.date) continue
      const dateObj = new Date(row.date)
      if (isNaN(dateObj.getTime())) continue
      const dateKey = formatDate(dateObj, { dateStyle: 'long' })
      groups[dateKey] ??= []
      groups[dateKey].push(row)
    }
    const sortedEntries = Object.entries(groups).sort(([, a], [, b]) => {
      const dateA = a[0]?.date ? new Date(a[0].date).getTime() : 0
      const dateB = b[0]?.date ? new Date(b[0].date).getTime() : 0
      return dateB - dateA
    })
    return sortedEntries
  }, [allRows])

  return (
    <ReadingHistoryList
      groupedByDate={groupedByDate}
      isPending={isPending}
      isError={isError}
      isEmpty={allRows.length === 0}
      isFetchingNextPage={isFetchingNextPage}
      hasNextPage={hasNextPage}
      pageSize={pageSize}
      refetch={refetch}
      fetchNextPage={fetchNextPage}
      className={className}
    />
  )
}
